/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import { Search, Trash2, Pencil, Plus, Users, ChevronRight } from "lucide-react";
import {
  useLeagues,
  useDeleteLeague,
  useCreateLeague,
  useUpdateLeague,
} from "@/hooks/api/useLeagues";
import TablePagination from "@/components/TablePagination";
import LeagueDetailDrawer from "@/pages/leagues/LeagueDetailDrawer";
import LeagueFormModal from "@/pages/leagues/LeagueFormModal";
import type { LeagueFilters } from "@/api/services/league.service";
import type { League } from "@/types/api.types";

export default function LeagueManagement() {
  const [filters, setFilters] = useState<LeagueFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(
    null,
  );
  const [formLeague, setFormLeague] = useState<League | null | undefined>(
    undefined,
  ); // undefined=closed, null=create, League=edit

  const { data, isLoading } = useLeagues(filters);
  const leagues = data?.leagues || [];
  const pagination = data?.pagination;
  const { mutate: deleteLeague } = useDeleteLeague();
  const { mutate: createLeague, isPending: isCreating } = useCreateLeague();
  const { mutate: updateLeague, isPending: isUpdating } = useUpdateLeague();

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`Delete league "${name}"? This cannot be undone.`)) return;
    deleteLeague(id, {
      onSuccess: () => toast.success("League deleted"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to delete"),
    });
  };

  const handleSave = (data: {
    name: string;
    isPrivate?: boolean;
    maxMembers?: number;
  }) => {
    if (formLeague) {
      // Edit mode
      updateLeague(
        { id: formLeague.id, data },
        {
          onSuccess: () => {
            toast.success("League updated");
            setFormLeague(undefined);
          },
          onError: (e: any) =>
            toast.error(e.response?.data?.message || "Failed to update"),
        },
      );
    } else {
      // Create mode
      createLeague(data, {
        onSuccess: () => {
          toast.success("League created");
          setFormLeague(undefined);
        },
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed to create"),
      });
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            League Management
          </h1>
          <p className="text-gray-500 mt-1">
            View and manage all user-created leagues
          </p>
        </div>
        <Button onClick={() => setFormLeague(null)}>
          <Plus className="h-4 w-4 mr-2" />
          Create League
        </Button>
      </div>

      {/* Search + Stats */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search leagues..."
            value={filters.search || ""}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                search: e.target.value,
                page: 1,
              }))
            }
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-gray-100 text-gray-700 font-medium">
            {pagination?.total ?? leagues.length} Total
          </Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">
            {leagues.filter((l) => !l.isPrivate).length} Public
          </Badge>
          <Badge className="bg-gray-200 text-gray-700 font-medium">
            {leagues.filter((l) => l.isPrivate).length} Private
          </Badge>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <div className="col-span-3">League Name</div>
          <div className="col-span-2">Join Code</div>
          <div className="col-span-2">Creator</div>
          <div className="col-span-1 text-center">Members</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1">Created</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {isLoading ? (
          [...Array(5)].map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b animate-pulse"
            >
              {[...Array(6)].map((_, j) => (
                <div key={j} className="col-span-2 h-4 bg-gray-200 rounded" />
              ))}
            </div>
          ))
        ) : leagues.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No leagues found</p>
          </div>
        ) : (
          leagues.map((league: League) => (
            <div
              key={league.id}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b last:border-0 hover:bg-gray-50 transition items-center"
            >
              <div className="col-span-3">
                <button
                  onClick={() => setSelectedLeagueId(league.id)}
                  className="font-semibold text-blue-600 hover:underline text-left flex items-center gap-1"
                >
                  {league.name}
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>

              <div className="col-span-2">
                <code className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-mono font-bold tracking-widest">
                  {league.code}
                </code>
              </div>

              <div className="col-span-2 text-sm text-gray-600">
                <p className="font-medium">{league.creator.name}</p>
                <p className="text-xs text-gray-400">
                  @{league.creator.username || "\u2014"}
                </p>
              </div>

              <div className="col-span-1 text-center">
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {league._count.members}
                </Badge>
              </div>

              <div className="col-span-2">
                <Badge
                  className={
                    league.isPrivate
                      ? "bg-gray-100 text-gray-700"
                      : "bg-green-100 text-green-700"
                  }
                >
                  {league.isPrivate ? "Private" : "Public"}
                </Badge>
              </div>

              <div className="col-span-1 text-xs text-gray-400">
                {new Date(league.createdAt).toLocaleDateString()}
              </div>

              <div className="col-span-1 flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormLeague(league)}
                  className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(league.id, league.name)}
                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {pagination && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
          onPageSizeChange={(limit) =>
            setFilters((prev) => ({ ...prev, limit, page: 1 }))
          }
        />
      )}

      {/* League Detail Drawer */}
      {selectedLeagueId && (
        <LeagueDetailDrawer
          leagueId={selectedLeagueId}
          onClose={() => setSelectedLeagueId(null)}
        />
      )}

      {/* League Create/Edit Modal */}
      {formLeague !== undefined && (
        <LeagueFormModal
          league={formLeague}
          isSaving={isCreating || isUpdating}
          onClose={() => setFormLeague(undefined)}
          onSave={handleSave}
        />
      )}
    </Layout>
  );
}
